import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { HeaderBanner as HeaderBannerType } from 'types/ui/HeaderBanner';
import HeaderBanner from './HeaderBanner';

interface AnnouncementBannerProps {
  headerBanner?: HeaderBannerType | null;
  onHeightChange?: (height: number) => void;
}

/** The announcement banners for the site. It can render one hardcoded banner (if it's not commented out), and then one
 * dynamic banner based on the'headerBanner' prop. The 'headerProp' is likely to be the referral banner.
 */
const AnnouncementBanners: React.FC<AnnouncementBannerProps> = ({
  headerBanner,
  onHeightChange,
}) => {
  const [height, setHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  let hardcodedBanner: HeaderBannerType | undefined;
  /*
  // The banner that we used after launching DRIP
  hardcodedBanner = {
    content: "We've recently upgraded our website! Spot any bugs?\u00a0🐛 Email our team at hello@daymade.co.uk.",
    faIconName: 'wrench',
    tappable: false,
    tapHandler: null,
  };
  */
  
  // Function to measure and update height
  const measureHeight = useCallback(() => {
    if (containerRef.current) {
      const newHeight = containerRef.current.offsetHeight;
      if (newHeight !== height) {
        setHeight(newHeight);
        onHeightChange?.(newHeight);
      }
    }
  }, [height, onHeightChange]);

  // Effect to measure height on mount and when dependencies change
  useEffect(() => {
    measureHeight();
  }, [headerBanner, measureHeight]);

  // Effect to set up ResizeObserver for responsive height changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      measureHeight();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureHeight]);

  return (
    <div ref={containerRef}>
      {hardcodedBanner &&
        <HeaderBanner
          bannerContent={hardcodedBanner}
          tappable={false}
          leftAligned={true}
        />
      }

      {/* The banner that is passed in as a prop */}
      {headerBanner && (
        <HeaderBanner
          bannerContent={headerBanner}
          tappable={headerBanner.tappable}
          leftAligned={true}
        />
      )}
    </div>
  );
};

export default AnnouncementBanners;
